/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { HIGH_LEVEL_ROW_BACKGROUND_COLOR } from 'features/MenuBook/constants';
import {
  menuBookEditorActions,
  selectMenuBookEditorExpandAll,
  selectMenuBookEditorFormSearchValue,
} from 'features/MenuBook/menuBookEditorSlice';
import { SubGenre } from 'features/MenuBook/models';
import { SubGenre as SubGenreDetail } from 'features/SubGenre/models/SubGenre';
import subGenreServiceDetail from 'features/SubGenre/pages/detail/services';
import React, { memo, useContext, useEffect, useState } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Button, Form } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MINUS_TEXT, PLUS_TEXT, PRIMARY_BUTTON } from 'utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils';
import DynamicMenuSettingModal from '../DynamicMenuSettingModal';
import { COLUMN_WIDTH, getItemStyle, MenuBookEditorModalContext } from '../MenuBookEditorModal';
import SubGenreDetailModal from '../SubGenreAddModal/components/Detail';
export interface SubGenreRowCollapseProps {
  genreId: number;
  genreName: string;
  subgenreId: number;
  children?: React.ReactNode;
  index: number;
  onSelectedRowChanged: () => void;
}

function SubGenreRow({
  genreId,
  genreName,
  subgenreId,
  children,
  index,
  onSelectedRowChanged,
}: SubGenreRowCollapseProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();
  const subgenre = useAppSelector((state) => {
    return state.menuBookEditor.data
      .flatMap((genre) => (genre.id === genreId ? genre.subgenres : []))
      .find((subgenre) => subgenre.id === subgenreId);
  }) as SubGenre;

  const isExpand = useAppSelector(selectMenuBookEditorExpandAll);
  const formSearchValue = useAppSelector(selectMenuBookEditorFormSearchValue);
  const [open, setOpen] = useState(true);

  // context
  const menuBookEditorModalContext = useContext(MenuBookEditorModalContext);

  // Subgenre Detail Modal
  const [showSubGenreDetailModal, setShowSubGenreDetailModal] = useState(false);
  const [subGenreModalDetailData, setSubGenreModalDetailData] = useState<{ subgenre: SubGenreDetail }>();

  // Dynamic Menu Setting Modal
  const [showDynamicMenuSettingModal, setShowDynamicMenuSettingModal] = useState(false);
  const [dynamicMenuSettingModalData, setDynamicMenuSettingModalData] = useState<{
    subGenre: SubGenre;
    dataDate: Date;
  }>();

  const pathId = `${genreId}-${subgenre.id}`;
  const droppableId = `genre-${genreId}`;
  const isSelected = false;
  const isGhosting = false;
  const mbEditorContentElement = document.getElementById('mb-editor-table');

  useEffect(() => {
    const btnExpandElem = document.querySelector(`#subgenre-${pathId}-row .dan-btn-expand`) as HTMLButtonElement;
    function handleBtnExpandElemClick(event: MouseEvent) {
      event.stopPropagation();
      setOpen(!open);
      btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
      btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
    }

    if (btnExpandElem) {
      btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
    }

    return () => {
      if (btnExpandElem) {
        btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (isExpand !== undefined) {
      setOpen(isExpand);
    }
  }, [isExpand]);

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // we are using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    // const targetElement = event.target as HTMLElement;
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    } else {
      return;
    }

    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an anchor
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    }

    dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: subgenre.id }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: subgenre.id }));
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.multiSelectTo({
          arg: {
            // data,
            // selectedItemIds,
            type: 'subgenre',
            droppableId: droppableId,
          },
          newItemId: subgenre.id,
        })
      );
      return;
    }

    dispatch(menuBookEditorActions.toggleSelection({ selectedDroppableId: droppableId, itemId: subgenre.id }));
  }

  function resetFocusAfterClose() {
    // focus on element with id="mb-editor-table-safe-focus"
    requestAnimationFrame(() => {
      const mbEditorContentElement = document.getElementById('mb-editor-table-safe-focus');
      mbEditorContentElement?.focus();
    });
  }

  // open subgenre modal events
  async function handleOpenSubgenreDetailModal() {
    const response = await subGenreServiceDetail.getSubGenre(subgenre.id.toString());
    setShowSubGenreDetailModal(true);
    setSubGenreModalDetailData({ subgenre: response.data });
  }

  function handleCloseSubGenreDetailModal(status: boolean, data?: SubGenreDetail) {
    setShowSubGenreDetailModal(false);
    setSubGenreModalDetailData(undefined);
    if (status && data) {
      dispatch(menuBookEditorActions.updateSubGenre(data));
    }
    resetFocusAfterClose();
  }

  // dynamic menu modal
  function handleOpenDynamicMenuSettingModal() {
    // check form change data
    const isMenuBookChanged = menuBookEditorModalContext?.checkFormDataChanged();
    if (isMenuBookChanged) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('MenuBookListPage.warning_save_data')}
          onConfirm={menuBookEditorModalContext?.handleSave.bind(null, false)}
        />
      ));
    } else {
      setShowDynamicMenuSettingModal(true);
      setDynamicMenuSettingModalData({
        subGenre: subgenre,
        dataDate: formSearchValue?.dataDate || new Date(),
      });
      resetFocusAfterClose();
    }
  }

  function handleCloseDynamicMenuSettingModal(isChanged: boolean) {
    setShowDynamicMenuSettingModal(false);
    setDynamicMenuSettingModalData(undefined);
    if (isChanged) {
      menuBookEditorModalContext?.loadMenuBookEditorData();
    }
  }

  return (
    <>
      <Draggable draggableId={`subgenre-${pathId}`} index={index}>
        {(provided, snapshot) => {
          return (
            <>
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={getItemStyle(
                  snapshot.isDragging,
                  provided.draggableProps.style,
                  isSelected,
                  isGhosting,
                  HIGH_LEVEL_ROW_BACKGROUND_COLOR
                )}
                onClick={onClick}
                onTouchEnd={onTouchEnd}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
              >
                <>
                  <div id={`subgenre-${pathId}-row`} className="dan-tr">
                    <div className="dan-td d-flex justify-content-center" style={{ width: `${COLUMN_WIDTH[0]}%` }}>
                      <Form.Check
                        className="chk-mbeditor-item custom-checkbox"
                        data-type="subgenre"
                        data-deletetype="subgenres"
                        data-genreid={genreId}
                        data-genre_name={genreName}
                        data-genre_subgenre_id={subgenre.genre_subgenre_id || '-1'}
                        data-subgenre_name={subgenre.name}
                        data-menu_ids={subgenre.menus.map((menu) => menu.id).join(',')}
                        defaultValue={subgenre.id}
                        tabIndex={-1}
                        onChange={onSelectedRowChanged}
                      ></Form.Check>
                    </div>
                    <div className="dan-td" style={{ width: `${COLUMN_WIDTH[1]}%` }}></div>
                    <div
                      className="dan-td"
                      style={{
                        width: `${COLUMN_WIDTH.slice(2, 10).reduce((sum, width) => sum + width, 0)}%`,
                      }}
                    >
                      <button
                        className="dan-btn-expand"
                        tabIndex={-1}
                        aria-controls={`subgenre-${pathId}-content`}
                        aria-expanded={false}
                      >
                        {open ? MINUS_TEXT : PLUS_TEXT}
                      </button>
                      <a
                        className="px-0 btn btn-link"
                        style={{ minWidth: 'inherit' }}
                        onClick={handleOpenSubgenreDetailModal}
                        rel="noreferrer"
                        tabIndex={-1}
                      >
                        {subgenre.name}
                      </a>
                      {subgenre.dynamic_menu_flg && (
                        <span className="d-block px-3 text-danger" title="メニュー表示サイズ可変対応">
                          ★
                        </span>
                      )}
                      {subgenre.menus.length > 0 && (
                        <Button
                          className="ms-3 position-relative"
                          style={{ zIndex: 1 }}
                          onClick={handleOpenDynamicMenuSettingModal}
                        >
                          設定
                        </Button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: open ? 'block' : 'none' }}>
                    <div id={`subgenre-${pathId}-content`}>{children}</div>
                  </div>
                </>
              </div>
            </>
          );
        }}
      </Draggable>

      {showSubGenreDetailModal &&
        subGenreModalDetailData &&
        createPortal(
          <SubGenreDetailModal
            sub_genre={subGenreModalDetailData.subgenre}
            show={showSubGenreDetailModal}
            handleCloseModalEdit={handleCloseSubGenreDetailModal}
          />,
          document.body
        )}

      {showDynamicMenuSettingModal &&
        dynamicMenuSettingModalData &&
        createPortal(
          <DynamicMenuSettingModal onClose={handleCloseDynamicMenuSettingModal} data={dynamicMenuSettingModalData} />,
          document.body
        )}
    </>
  );
}

export default memo(SubGenreRow);
